import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { fromJS } from "immutable";
import { updateTimelineTime, updateProjectData } from "../../../../redux/actions/timelineUtils";
import { buildLocalSmartfocus } from "../../../timeline/timeline-helper";
import { ScaleHeader, SearchInput, SearchInputContainer, SfScaleContainer } from "./smartfocus-components";
import vmTheme from "../../../../constants/theme";
import Slider from "../../../zoom/zoom-slider";
import Typography from "../../../../common-components/Typography";
import { font } from "../../../../constants/font";
import { setWorkspaceTextFocus } from "../../../../redux/actions/appUtils";
import useMobileDetection from "../../../../helper/hooks/useMobileDetection";

const SmartfocusScale = ({ handleScaleValue, isLock }) => {
    const dispatch = useDispatch();
    const selectedWorkspaceItem = useSelector((state) => state.app.get("selectedItems"));
    const selectedValue = useSelector((state) => state.app.get("selectedSubtitles"));
    const selectedItem = selectedValue.size > 0 ? selectedValue?.get(0)?.split("-")[0] : selectedWorkspaceItem.get(0);
    const isMobile = useMobileDetection();
    const item = useSelector((state) =>
        state.projectDetails.getIn(["workspaceItems", selectedItem])
    );

    const project = useSelector((state) => state.projectDetails);
    const playhead = useSelector((state) => state.app.get("playhead"));
    const zoomData = item?.get("zoomData")?.toJS() || {};
    const projectWidth = project.get("width");
    const projectHeight = project.get("height");

    let pWidth = project.get("width");
    if (projectWidth < projectHeight && item && (item?.get("width") > item?.get("height"))) {
        pWidth = item.get("width");
    }
    if (project.get("width") === project.get("height")) {
        pWidth = project.get("width");
    }

    // Initialize tolerance with default value, will be updated in useEffect
    const [tolerance, setTolerance] = useState(30);
    const [toleranceInput, setToleranceInput] = useState('50');
    // Calculate initial zoom speed from existing zoom data
    const calculateInitialZoomSpeed = () => {
        if (!zoomData) return 0.6; // Default fallback

        const zoomDataKeys = Object.keys(zoomData);
        if (!zoomDataKeys.length) return 0.6;

        // Find the group that contains playhead
        const matchedGroupKey = zoomDataKeys.find((key) => {
            const group = zoomData[key];
            const subKeys = Object.keys(group);

            const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");

            return (
                zoomIn &&
                zoomOut &&
                playhead >= zoomIn.start &&
                playhead <= zoomOut.end
            );
        });

        if (!matchedGroupKey) return 0.6;

        const matchedGroup = zoomData[matchedGroupKey];
        const subKeys = Object.keys(matchedGroup);
        const zoomIn = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomIn");
        const zoomOut = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomOut");

        if (!zoomIn || !zoomOut) return 0.6;

        // Calculate zoom speed as the duration of zoomIn
        return zoomIn.end - zoomIn.start;
    };

    const [zoomSpeed, setZoomSpeed] = useState(calculateInitialZoomSpeed().toFixed(1));
    const [isZoomSpeedInvalid, setIsZoomSpeedInvalid] = useState(false);
    const [isDraggingZoomSpeed, setIsDraggingZoomSpeed] = useState(false);
    const [isEditingScale, setIsEditingScale] = useState(false);
    const [isEditingZoomSpeed, setIsEditingZoomSpeed] = useState(false);

    const findCurrentZoomGroup = (zoomData, playhead, itemEnter) => {
        const zoomDataKeys = Object.keys(zoomData);
        if (!zoomDataKeys.length) return null;
        return zoomDataKeys.find((key) => {
            const group = zoomData[key];
            const subKeys = Object.keys(group);

            const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");
            return (
                zoomIn &&
                zoomOut &&
                playhead >= (zoomIn.start + itemEnter) &&
                playhead <= (zoomOut.end + itemEnter)
            );
        });
    };
    const updateScaleAndZoomSpeed = (group, pWidth, tolerance, handleScaleValue) => {
        const subKeys = Object.keys(group);
        const zoomIn = subKeys.map((k) => group[k]).find((i) => i.type === "zoomIn");
        if (zoomIn) {
            let scaleValue;
            if (projectHeight > projectWidth) {
                scaleValue = (zoomIn.height / project.get("height")) * 100;
            } else {
                scaleValue = (zoomIn.width / pWidth) * 100;
            }
            if (Math.abs(scaleValue - tolerance) >= 0) {
                const scale = Math.max(100 - Math.round(scaleValue), 0);
                const a = Math.min(Math.round(scale / 70 * 100), 100);
                const b = Math.round(Math.max(100 - scaleValue, 0));
                const toleranceValue = Math.min(Math.round(b / 70 * 100), 100);
                // Only update scale inputs if user is not actively editing
                if (!isEditingScale) {
                    setTolerance(toleranceValue);
                    setToleranceInput(a.toString());
                    handleScaleValue(Math.max(scaleValue, 0));
                }
            }

            // Only update zoom speed if user is not currently dragging the slider or editing the input
            if (!isDraggingZoomSpeed && !isEditingZoomSpeed) {
                setZoomSpeed(Math.max(0.1, zoomIn.end - zoomIn.start).toFixed(1));
            }
        }
    };
    // Update scale value based on playhead and zoom range
    useEffect(() => {
        if (!zoomData || !item) return;
        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const currentZoomGroupKey = findCurrentZoomGroup(zoomData, playhead, itemEnter);
        if (currentZoomGroupKey) {
            const group = zoomData[currentZoomGroupKey];
            updateScaleAndZoomSpeed(group, pWidth, tolerance, handleScaleValue);
        }
    }, [playhead, item]);

    // Initialize with default scale value, will be updated based on zoom data
    useEffect(() => {
        if (!zoomData || !item) return;
        const enterStart = item.get("enterStart") || 0;
        const videoStart = item.get("videoStart") || 0;
        const itemEnter = enterStart - videoStart;

        const currentZoomGroup = Object.entries(zoomData).find(([, group]) => {
            const subKeys = Object.keys(group);
            const zoomIn = subKeys.map((k) => group[k]).find((i) => i.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((i) => i.type === "zoomOut");
            return (
                zoomIn &&
                zoomOut &&
                playhead >= (zoomIn.start + itemEnter) &&
                playhead <= (zoomOut.end + itemEnter)
            );
        });

        if (currentZoomGroup) {
            const group = currentZoomGroup[1];
            updateScaleAndZoomSpeed(group, pWidth, tolerance, handleScaleValue);
        }
    }, [playhead, item]);

    const theme = useSelector((state) => state.app.get("theme"));
    const [isInvalid, setIsInvalid] = useState(false);

    const handleTolerance = (e) => {
        const inputValue = e.target.value;
        if (inputValue.length > 4) return;
        setIsEditingScale(true);
        setToleranceInput(inputValue);
        if (inputValue === '') {
            setIsInvalid(false);
            return;
        }

        const numericValue = parseInt(inputValue, 10);
        const scaledValue = numericValue * 0.7;
        if (Number.isNaN(numericValue) || numericValue < 0 || numericValue > 100) {
            setIsInvalid(true);
            setTolerance(tolerance)
            return;
        }
        setIsInvalid(false);
        setTolerance(Math.max(numericValue, 0));
        handleScaleValue(Math.max(100 - scaledValue, 0));
    };

    const getMaxZoomSpeed = () => {
        const zoomDataKeys = Object.keys(zoomData);
        if (!zoomDataKeys.length) return 10;
        const matchedGroupKey = zoomDataKeys.find((key) => {
            const group = zoomData[key];
            const subKeys = Object.keys(group);
            const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");
            return (
                zoomIn &&
                zoomOut &&
                playhead >= zoomIn.start &&
                playhead <= zoomOut.end
            );
        });

        if (!matchedGroupKey) return 10;

        const matchedGroup = zoomData[matchedGroupKey];
        const subKeys = Object.keys(matchedGroup);
        const zoomIn = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomIn");
        const zoomOut = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomOut");

        if (!zoomIn || !zoomOut) return 10; // Default fallback

        const totalDuration = zoomOut.end - zoomIn.start;
        return Math.min((totalDuration / 2).toFixed(1), 60);
    };

    const handleSliderChange = (e, value) => {
        if (value < 0 || value > 100) return; // Ensure value is within bounds
        setIsEditingScale(true);
        setTolerance(value);
        setToleranceInput(value.toString());
        const scaledValue = value * 0.7;
        handleScaleValue(Math.max(100 - scaledValue, 0));
    };

    const handleSliderMouseUp = () => {
        // This is called when user releases the slider
        // The visual updates have already been applied via handleSliderChange
        // No need for additional Redux updates here since scale changes are visual only
        setIsEditingScale(false);
    };

    const handleZoomSpeed = (e) => {
        const inputValue = e.target.value;
        // Prevent entering more than 3 characters (for "100")
        if (inputValue.length > 4) return;
        // Allow only numbers and one decimal point with one digit after
        if (!/^\d*\.?\d{0,1}$/.test(inputValue)) {
            return;
        }
        setIsEditingZoomSpeed(true);
        setZoomSpeed(inputValue);

        // Only validate, don't update Redux on every keystroke
        if (inputValue === '') {
            setIsZoomSpeedInvalid(false);
            return;
        }

        const value = parseFloat(inputValue);
        const maxZoomSpeed = parseFloat(getMaxZoomSpeed());

        // Check if value is a valid number and within range
        if (Number.isNaN(value) || value < 0.1 || value > maxZoomSpeed) {
            setIsZoomSpeedInvalid(true);
        } else {
            setIsZoomSpeedInvalid(false);
        }
    };

    const handleZoomSpeedBlur = () => {
        setIsEditingZoomSpeed(false);
        dispatch(setWorkspaceTextFocus(false));

        const value = parseFloat(zoomSpeed);
        if (Number.isNaN(value) || zoomSpeed === '') {
            // Reset to current zoom data value
            const zoomDataKeys = Object.keys(zoomData);
            if (zoomDataKeys.length) {
                const matchedGroupKey = zoomDataKeys.find((key) => {
                    const group = zoomData[key];
                    const subKeys = Object.keys(group);
                    const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
                    const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");
                    return zoomIn && zoomOut && playhead >= zoomIn.start && playhead <= zoomOut.end;
                });
                if (matchedGroupKey) {
                    const group = zoomData[matchedGroupKey];
                    const subKeys = Object.keys(group);
                    const zoomIn = subKeys.map((k) => group[k]).find((i) => i.type === "zoomIn");
                    if (zoomIn) {
                        setZoomSpeed(Math.max(0.1, zoomIn.end - zoomIn.start).toFixed(1));
                    }
                }
            }
            return;
        }

        const maxZoomSpeed = parseFloat(getMaxZoomSpeed());
        if (value < 0.1 || value > maxZoomSpeed) {
            setIsZoomSpeedInvalid(true);
            return;
        }

        const zoomDataKeys = Object.keys(zoomData);
        if (!zoomDataKeys.length) return;

        const updatedZoomData = { ...zoomData };

        const matchedGroupKey = zoomDataKeys.find((key) => {
            const group = updatedZoomData[key];
            const subKeys = Object.keys(group);
            const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");
            return zoomIn && zoomOut && playhead >= zoomIn.start && playhead <= zoomOut.end;
        });

        if (!matchedGroupKey) return;

        const matchedGroup = updatedZoomData[matchedGroupKey];
        const subKeys = Object.keys(matchedGroup);
        const zoomIn = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomIn");
        const zoomOut = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomOut");
        const totalDuration = zoomOut.end - zoomIn.start;
        const allowedMax = totalDuration / 2;

        if (value <= allowedMax) {
            // Update zoomIn.end and zoomOut.start based on zoom speed
            zoomIn.end = parseFloat((zoomIn.start + value).toFixed(2));
            zoomOut.start = parseFloat((zoomOut.end - value).toFixed(2));

            const updatedProject = project.setIn(
                ["workspaceItems", item.get("id"), "zoomData"],
                fromJS(updatedZoomData)
            );

            const toUpdate = [
                {
                    id: item.get("id"),
                    container: "workspaceItems",
                    toUpdate: {
                        zoomData: updatedZoomData,
                    },
                },
            ];

            if (toUpdate.length) {
                dispatch(updateTimelineTime({ toUpdate }));
                const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
                const updatedProjects = updatedLocalZoom.getIn(["localZoom"]);
                dispatch(
                    updateProjectData({
                        localZoom: updatedProjects,
                    })
                );
            }
        }
    };

    const handleZoomSpeedSliderChange = (e, value) => {
        if (value < 0.1) return;
        setIsDraggingZoomSpeed(true);
        setZoomSpeed(value.toFixed(1));
        setIsZoomSpeedInvalid(false);

        // Update Redux immediately for smooth feedback
        const numericValue = parseFloat(value);
        const maxZoomSpeed = parseFloat(getMaxZoomSpeed());
        if (Number.isNaN(numericValue) || numericValue > maxZoomSpeed) {
            setIsZoomSpeedInvalid(true);
            return;
        }

        const zoomDataKeys = Object.keys(zoomData);
        if (!zoomDataKeys.length) return;

        const updatedZoomData = { ...zoomData };

        const matchedGroupKey = zoomDataKeys.find((key) => {
            const group = updatedZoomData[key];
            const subKeys = Object.keys(group);

            const zoomIn = subKeys.map((k) => group[k]).find((item) => item.type === "zoomIn");
            const zoomOut = subKeys.map((k) => group[k]).find((item) => item.type === "zoomOut");

            return (
                zoomIn &&
                zoomOut &&
                playhead >= zoomIn.start &&
                playhead <= zoomOut.end
            );
        });

        if (!matchedGroupKey) return;

        const matchedGroup = updatedZoomData[matchedGroupKey];
        const subKeys = Object.keys(matchedGroup);
        const zoomIn = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomIn");
        const zoomOut = subKeys.map((k) => matchedGroup[k]).find((i) => i.type === "zoomOut");
        const totalDuration = zoomOut.end - zoomIn.start;
        const allowedMax = totalDuration / 2;

        if (numericValue <= allowedMax) {
            zoomIn.end = parseFloat((zoomIn.start + numericValue).toFixed(2));
            zoomOut.start = parseFloat((zoomOut.end - numericValue).toFixed(2));

            const updatedProject = project.setIn(
                ["workspaceItems", item.get("id"), "zoomData"],
                fromJS(updatedZoomData)
            );

            const toUpdate = [
                {
                    id: item.get("id"),
                    container: "workspaceItems",
                    toUpdate: {
                        zoomData: updatedZoomData,
                    },
                },
            ];

            if (toUpdate.length) {
                dispatch(updateTimelineTime({ toUpdate }));
                const updatedLocalZoom = buildLocalSmartfocus(updatedProject);
                const updatedProjects = updatedLocalZoom.getIn(["localZoom"]);

                dispatch(
                    updateProjectData({
                        localZoom: updatedProjects,
                    })
                );
            }
        }
    };

    const handleZoomSpeedSliderMouseUp = () => {
        // Clear dragging state
        setIsDraggingZoomSpeed(false);
    }

    return (
        <SfScaleContainer>
            <ScaleHeader>
                <Typography
                    content={"Scale"}
                    color={vmTheme[theme].panelPrimaryColor}
                    display="block"
                    font={font.mediumMini}
                />
                <SearchInputContainer style={{ pointerEvents: isLock ? 'none' : 'auto' }}>
                    <SearchInput
                        type="number"
                        value={toleranceInput}
                        placeholder="50%"
                        min="0"
                        max="100"
                        onKeyDownCapture={(e) => { e.stopPropagation() }}
                        onKeyDown={(e) => { e.stopPropagation() }}
                        onKeyUpCapture={(e) => { e.stopPropagation() }}
                        onKeyUp={(e) => { e.stopPropagation() }}
                        onChange={handleTolerance}
                        onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                        onBlur={() => {
                            dispatch(setWorkspaceTextFocus(false));
                            setIsEditingScale(false);
                        }}
                        style={{
                            width: toleranceInput.length > 3 ? '72px' : toleranceInput.length > 2 ? '62px' : '62px',
                            borderColor: isInvalid ? '#ff4d4f' : undefined,
                            boxShadow: isInvalid ? '0 0 0 1px #ff4d4f' : undefined,
                            font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                        }}
                    />
                    {
                        toleranceInput !== "" && (
                            <span style={{
                                margin: toleranceInput.length > 3 ? "0px 0px 0px 8px" : toleranceInput.length > 2 ? "0px 0px 0px 3px" : toleranceInput.length > 1 ? "0px 0px 0px 7px" : "0px",
                                position: "absolute",
                                left: isMobile ? "25px" : toleranceInput.length > 2 ? "27px" : "18px",
                                top: isMobile ? "10px" : "11px",
                                color: vmTheme[theme].panelPrimaryColor,
                                font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                            }}>%</span>
                        )
                    }
                </SearchInputContainer>
            </ScaleHeader>

            <div className="slider" style={{ margin: "0px 0px 0px 17px", pointerEvents: isLock ? 'none' : 'auto' }}>
                <Slider
                    min={0}
                    max={100}
                    inputMin={0}
                    inputMax={100}
                    value={tolerance}
                    step={1}
                    onChangeSliderValue={handleSliderChange}
                    onMouseUp={handleSliderMouseUp}
                    border={"none"}
                    borderRadius={"3px"}
                    background={vmTheme[theme].veryLightGray}
                    margin={"0"}
                    height={"5px"}
                    width={"316px"}
                    thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "16px",
                        width: "16px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                    }}
                    progressBackground={vmTheme[theme].secondaryBorderColor}
                    isChangeBg={true}
                />
            </div>
            <ScaleHeader>
                <Typography
                    content={"Zoom Speed"}
                    color={vmTheme[theme].panelPrimaryColor}
                    display="block"
                    font={font.mediumMini}
                />
                <SearchInputContainer style={{ pointerEvents: isLock ? 'none' : 'auto' }}>
                    <SearchInput
                        type="number"
                        value={zoomSpeed}
                        onKeyDownCapture={(e) => { e.stopPropagation() }}
                        onKeyDown={(e) => { e.stopPropagation() }}
                        onKeyUpCapture={(e) => { e.stopPropagation() }}
                        onKeyUp={(e) => { e.stopPropagation() }}
                        placeholder={zoomSpeed ? `0.3-${getMaxZoomSpeed()}s` : '0.3s'}
                        onChange={handleZoomSpeed}
                        onFocus={() => dispatch(setWorkspaceTextFocus(true))}
                        onBlur={handleZoomSpeedBlur}
                        min="0.1"
                        max={getMaxZoomSpeed()}
                        step="0.1"
                        style={{
                            width: zoomSpeed.length > 3 ? '72px' : zoomSpeed.length > 2 ? '62px' : '62px',
                            borderColor: isZoomSpeedInvalid ? '#ff4d4f' : undefined,
                            boxShadow: isZoomSpeedInvalid ? '0 0 0 1px #ff4d4f' : undefined,
                            font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                        }}
                    />
                    {
                        zoomSpeed !== "" && (
                            <span style={{
                                margin: zoomSpeed.length > 3 ? "0px 0px 0px 14px" : zoomSpeed.length > 2 ? "0px 0px 0px 10px" : "0px 0px 0px 12px",
                                position: "absolute",
                                left: isMobile ? "24px" : "22px",
                                top: isMobile ? "10px" : "11px",
                                color: vmTheme[theme].panelPrimaryColor,
                                font: isMobile ? font.mediumMini_17 : font.normalMiniBase,
                            }}>s</span>
                        )
                    }
                </SearchInputContainer>
            </ScaleHeader>
            <div className="slider" style={{ margin: "0px 0px 0px 17px", pointerEvents: isLock ? 'none' : 'auto' }}>
                <Slider
                    min={0.1}
                    max={getMaxZoomSpeed()}
                    inputMin={0.1}
                    inputMax={getMaxZoomSpeed()}
                    value={parseFloat(zoomSpeed) || 0.1}
                    step={0.1}
                    onChangeSliderValue={handleZoomSpeedSliderChange}
                    onMouseUp={handleZoomSpeedSliderMouseUp}
                    border={"none"}
                    borderRadius={"3px"}
                    background={vmTheme[theme].veryLightGray}
                    margin={"0"}
                    height={"5px"}
                    width={"316px"}
                    thumb={{
                        background: `${vmTheme[theme].polarColor} 0% 0% no-repeat padding-box`,
                        border: `2px solid ${vmTheme[theme].secondaryBorderColor}`,
                        height: "16px",
                        width: "16px",
                        hoverBG: `${vmTheme[theme].secondaryBorderColor}`,
                        hoverBorder: `1.7px solid ${vmTheme[theme].secondaryBorderColor}`,
                    }}
                    progressBackground={vmTheme[theme].secondaryBorderColor}
                    isChangeBg={true}
                />
            </div>
        </SfScaleContainer>
    );
};

SmartfocusScale.propTypes = {
    handleScaleValue: PropTypes.func.isRequired,
    isLock: PropTypes.bool
};

export default SmartfocusScale;